import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Table, Spinner, Alert, Pagination } from 'react-bootstrap';
import { api } from '../services/api';
import { formatDateRu, formatDateTimeRu } from '../utils/dateFormat';
import { formatSubjectName } from '../utils/formatSubjectName';
import TzProductDetailModal, { TzConfirmedProduct } from './TzProductDetailModal';

const PAGE_SIZE = 20;

export type SummaryDetailProduct = {
  id: number;
  article: string;
  name: string;
  new_wave: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  subject_name?: string;
};

interface SummaryDetailModalProps {
  show: boolean;
  onHide: () => void;
  metric: 'inWork' | 'statusChanges' | 'newProducts' | 'tzConfirmed' | null;
  metricLabel: string;
  dateFrom: string;
  dateTo: string;
  userId?: string;
}

const SummaryDetailModal: React.FC<SummaryDetailModalProps> = ({
  show,
  onHide,
  metric,
  metricLabel,
  dateFrom,
  dateTo,
  userId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<SummaryDetailProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<TzConfirmedProduct | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!show || !metric) {
      setProducts([]);
      setError(null);
      setPage(1);
      return;
    }
    setPage(1);
    setLoading(true);
    setError(null);
    api
      .getWeeklyDetail(metric, dateFrom, dateTo, userId)
      .then((res) => {
        if (res.success) {
          setProducts(res.products || []);
        } else {
          setProducts([]);
          setError('Не удалось загрузить данные');
        }
      })
      .catch((e: any) => {
        setProducts([]);
        setError(e?.response?.data?.error || e?.message || 'Ошибка загрузки');
      })
      .finally(() => setLoading(false));
  }, [show, metric, dateFrom, dateTo, userId]);

  const periodLabel = dateFrom && dateTo ? `Период: ${formatDateRu(dateFrom)} — ${formatDateRu(dateTo)}` : '';
  const title = `${metricLabel} — потоварная детализация`;

  const totalPages = Math.max(1, Math.ceil(products.length / PAGE_SIZE));
  const paginatedProducts = useMemo(
    () => products.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [products, page]
  );

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {periodLabel && metric !== 'inWork' && <p className="text-muted small mb-2">{periodLabel}</p>}
          <p className="text-muted small mb-2">Клик по строке — карточка товара и история (как в «По предметам»).</p>
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          )}
          {error && <Alert variant="danger">{error}</Alert>}
          {!loading && !error && (
            products.length === 0 ? (
              <p className="text-muted mb-0">Нет товаров.</p>
            ) : (
              <>
                <p className="text-muted small mb-2">
                  Показано {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, products.length)} из {products.length}
                </p>
                <Table responsive size="sm" hover>
                  <thead>
                    <tr>
                      <th>Артикул</th>
                      <th>Название</th>
                      <th>Тип</th>
                      <th>Предмет</th>
                      <th>Дата добавления</th>
                      <th>Дата обновления</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProducts.map((p) => (
                      <tr
                        key={p.id}
                        role="button"
                        tabIndex={0}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedProduct({ id: p.id, article: p.article, new_wave: p.new_wave })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedProduct({ id: p.id, article: p.article, new_wave: p.new_wave });
                          }
                        }}
                      >
                        <td>{p.article || '—'}</td>
                        <td className="text-break">{p.name || '—'}</td>
                        <td>{p.new_wave ? <span className="badge bg-info">New Wave</span> : '—'}</td>
                        <td>{formatSubjectName(p.subject_name)}</td>
                        <td>{formatDateTimeRu(p.created_at)}</td>
                        <td>{formatDateTimeRu(p.updated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                    <Pagination size="sm" className="mb-0">
                      <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                      <Pagination.Prev disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />
                      <Pagination.Item active>{page} / {totalPages}</Pagination.Item>
                      <Pagination.Next disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                      <Pagination.Last disabled={page >= totalPages} onClick={() => setPage(totalPages)} />
                    </Pagination>
                  </div>
                )}
              </>
            )
          )}
        </Modal.Body>
      </Modal>
      <TzProductDetailModal
        show={!!selectedProduct}
        onHide={() => setSelectedProduct(null)}
        product={selectedProduct}
      />
    </>
  );
};

export default SummaryDetailModal;
